/**
 * Data Source Form Component
 *
 * Form for creating and editing data source configurations
 * with type-specific fields and validation.
 */

import {
  ExpandMore as ExpandMoreIcon,
  Science as TestIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import { useDataSourceTypes } from '../../hooks/useDataSourceTypes';

interface DataSourceConfiguration {
  [key: string]: any;
}

interface SyncSchedule {
  enabled: boolean;
  cronExpression: string;
  incrementalSyncEnabled: boolean;
  incrementalSyncHours: number;
}

interface DataSourceFormData {
  name: string;
  type: string;
  organizationId: string;
  enabled: boolean;
  configuration: DataSourceConfiguration;
  syncSchedule: SyncSchedule;
}

interface DataSourceFormProps {
  dataSource?: DataSourceFormData;
  onSubmit: (data: DataSourceFormData) => void;
  onCancel: () => void;
}

export const DataSourceForm: React.FC<DataSourceFormProps> = ({
  dataSource,
  onSubmit,
  onCancel,
}) => {
  const { dataSourceTypes, loading: typesLoading } = useDataSourceTypes();
  const [formData, setFormData] = useState<DataSourceFormData>({
    name: '',
    type: 'azure-devops',
    organizationId: 'default-org',
    enabled: true,
    configuration: {},
    syncSchedule: {
      enabled: true,
      cronExpression: '0 */6 * * *',
      incrementalSyncEnabled: true,
      incrementalSyncHours: 24,
    },
  });
  const [validationErrors, setValidationErrors] = useState<any>({});
  const [testResult, setTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (dataSource) {
      setFormData(dataSource);
    }
  }, [dataSource]);

  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev: any) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleConfigurationChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        [field]: value,
      },
    }));
  };

  const handleSyncScheduleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      syncSchedule: {
        ...prev.syncSchedule,
        [field]: value,
      },
    }));
  };

  const validateForm = () => {
    const errors: any = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.organizationId.trim()) {
      errors.organizationId = 'Organization ID is required';
    }

    // Type-specific validation
    const selectedType = dataSourceTypes.find(t => t.type === formData.type);
    if (selectedType) {
      selectedType.requiredFields.forEach(field => {
        if (!formData.configuration?.[field]) {
          errors[`configuration.${field}`] = `${field} is required`;
        }
      });
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleTestConnection = async () => {
    if (!validateForm()) {
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      // Mock test connection - in real app, would call API
      await new Promise(resolve => setTimeout(resolve, 2000));

      setTestResult({
        success: true,
        responseTime: 150,
        message: 'Connection successful',
      });
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.message || 'Connection failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const selectedType = dataSourceTypes.find(t => t.type === formData.type);

  if (typesLoading) {
    return (
      <Box display='flex' justifyContent='center' p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component='form' onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {/* Basic Information */}
      <Typography variant='h6' gutterBottom>
        Basic Information
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label='Name'
            value={formData.name}
            onChange={e => handleFieldChange('name', e.target.value)}
            error={!!validationErrors.name}
            helperText={validationErrors.name}
            required
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={formData.type}
              label='Type'
              onChange={e => handleFieldChange('type', e.target.value)}
            >
              {dataSourceTypes.map(type => (
                <MenuItem key={type.type} value={type.type}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            label='Organization ID'
            value={formData.organizationId}
            onChange={e => handleFieldChange('organizationId', e.target.value)}
            error={!!validationErrors.organizationId}
            helperText={validationErrors.organizationId}
            required
          />
        </Grid>
        <Grid size={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.enabled}
                onChange={e => handleFieldChange('enabled', e.target.checked)}
              />
            }
            label='Enabled'
          />
        </Grid>
      </Grid>

      {/* Type-specific Configuration */}
      {selectedType && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant='h6'>
              {selectedType.name} Configuration
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              {selectedType.description}
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant='subtitle2' gutterBottom>
                Capabilities:
              </Typography>
              <Box display='flex' gap={1} flexWrap='wrap'>
                {selectedType.capabilities.map(capability => (
                  <Chip
                    key={capability}
                    label={capability}
                    size='small'
                    variant='outlined'
                  />
                ))}
              </Box>
            </Box>

            <Grid container spacing={2}>
              {selectedType.requiredFields.map(field => (
                <Grid size={12} key={field}>
                  <TextField
                    fullWidth
                    label={field
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, str => str.toUpperCase())}
                    type={
                      field.toLowerCase().includes('token') ||
                      field.toLowerCase().includes('password')
                        ? 'password'
                        : 'text'
                    }
                    value={formData.configuration?.[field] || ''}
                    onChange={e =>
                      handleConfigurationChange(field, e.target.value)
                    }
                    error={!!validationErrors[`configuration.${field}`]}
                    helperText={validationErrors[`configuration.${field}`]}
                    required
                  />
                </Grid>
              ))}

              {selectedType.optionalFields.map(field => (
                <Grid size={{ xs: 12, sm: 6 }} key={field}>
                  <TextField
                    fullWidth
                    label={field
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, str => str.toUpperCase())}
                    value={formData.configuration?.[field] || ''}
                    onChange={e =>
                      handleConfigurationChange(field, e.target.value)
                    }
                  />
                </Grid>
              ))}
            </Grid>

            {/* Connection Test */}
            <Box sx={{ mt: 3 }}>
              <Button
                variant='outlined'
                startIcon={
                  testing ? <CircularProgress size={16} /> : <TestIcon />
                }
                onClick={handleTestConnection}
                disabled={testing}
              >
                {testing ? 'Testing...' : 'Test Connection'}
              </Button>

              {testResult && (
                <Alert
                  severity={testResult.success ? 'success' : 'error'}
                  sx={{ mt: 2 }}
                >
                  {testResult.message}
                  {testResult.responseTime && (
                    <Typography variant='caption' display='block'>
                      Response time: {testResult.responseTime}ms
                    </Typography>
                  )}
                </Alert>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Sync Schedule Configuration */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant='h6'>Sync Schedule</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.syncSchedule?.enabled || false}
                    onChange={e =>
                      handleSyncScheduleChange('enabled', e.target.checked)
                    }
                  />
                }
                label='Enable automatic sync'
              />
            </Grid>

            {formData.syncSchedule?.enabled && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label='Cron Expression'
                    value={formData.syncSchedule?.cronExpression || ''}
                    onChange={e =>
                      handleSyncScheduleChange('cronExpression', e.target.value)
                    }
                    helperText="e.g., '0 */6 * * *' for every 6 hours"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    type='number'
                    label='Incremental Sync Hours'
                    value={formData.syncSchedule?.incrementalSyncHours || 24}
                    onChange={e =>
                      handleSyncScheduleChange(
                        'incrementalSyncHours',
                        parseInt(e.target.value)
                      )
                    }
                    helperText='Look back hours for incremental sync'
                  />
                </Grid>
                <Grid size={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          formData.syncSchedule?.incrementalSyncEnabled || false
                        }
                        onChange={e =>
                          handleSyncScheduleChange(
                            'incrementalSyncEnabled',
                            e.target.checked
                          )
                        }
                      />
                    }
                    label='Enable incremental sync'
                  />
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Form Actions */}
      <Box display='flex' justifyContent='flex-end' gap={2} sx={{ mt: 3 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type='submit' variant='contained'>
          {dataSource ? 'Update' : 'Create'}
        </Button>
      </Box>
    </Box>
  );
};
